/**
 * PractitionersScroller — Iter 132.
 *
 * Compact horizontal scroller for the patient dashboard. Replaces the
 * former standalone `/patient/practitioners` route by showing the roster
 * inline where patients spend most of their time. Data source is
 * `GET /api/practitioners/search` — same endpoint the removed page used.
 *
 * Each card shows the clinician's avatar, name, discipline chips and
 * next-available slot. Tapping a card jumps into the booking flow with
 * the clinician pre-selected (`/patient/book?clinician=<id>`).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { User, Calendar, ChevronRight, Users } from 'lucide-react';

export default function PractitionersScroller() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/practitioners/search')
      .then(r => { if (!cancelled) setRows(r.data || []); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, []);

  if (rows === null) {
    return (
      <div className="bento-card" data-testid="practitioners-scroller-loading">
        <div className="flex items-center gap-3 mb-3">
          <Users className="w-4 h-4 text-brand"/>
          <div className="font-heading font-semibold text-lg">Practitioners</div>
        </div>
        <div className="text-sm text-fg-muted">Loading roster…</div>
      </div>
    );
  }
  if (rows.length === 0) return null;

  return (
    <div className="bento-card" data-testid="practitioners-scroller">
      <div className="flex items-center gap-3 mb-3">
        <Users className="w-4 h-4 text-brand"/>
        <div className="flex-1 font-heading font-semibold text-lg">Our practitioners</div>
        <div className="text-[11px] uppercase tracking-wider text-fg-muted">{rows.length} on the team</div>
      </div>
      {/* Iter 132 — horizontal scroller. Snap-scrolling on touch, keeps
          the dashboard density low. */}
      <div
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1 scrollbar-hide"
        data-testid="practitioners-scroller-track"
      >
        {rows.map((p) => (
          <Link
            key={p.user_id}
            to={`/patient/book?clinician=${p.user_id}`}
            className="flex-none w-[220px] snap-start rounded-lg border border-soft hover:border-brand/40 hover:shadow-sm transition p-3 group"
            data-testid={`practitioner-card-${p.user_id}`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-brand/10 grid place-items-center overflow-hidden shrink-0">
                {p.avatar_url
                  ? <img src={p.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover"/>
                  : <User className="w-5 h-5 text-brand"/>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate text-sm">{p.name}</div>
                <div className="text-[11px] text-fg-muted truncate">
                  {(p.disciplines || []).map(d => d.replace('_', ' ')).join(' · ') || 'Generalist'}
                </div>
              </div>
            </div>
            {p.bio && <div className="text-[11px] text-fg-muted line-clamp-2 mb-2">{p.bio}</div>}
            <div className="flex items-center justify-between pt-2 border-t border-soft/60">
              <div className="text-[11px] text-fg-muted">
                {p.next_available_at ? (
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-brand"/>
                    {new Date(p.next_available_at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                ) : (
                  <span>Fully booked 14d+</span>
                )}
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-brand opacity-0 group-hover:opacity-100 transition"/>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
